import { useEffect, useState } from "react";
import {
  Container,
  Paper,
  Table,
  TableRow,
  TableCell,
  TableHead,
  TableBody,
  Typography,
  Button,
  IconButton,
  Box,
  Chip,
  MenuItem,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import FormModal from "../components/FormModal";
import api from "../api/axios";
import Can from "../auth/Can";
import { useNotify } from "../context/NotificationContext";

export default function Users() {
  const { notify } = useNotify();
  const [users, setUsers] = useState([]);
  const [open, setOpen] = useState(false);
  const [current, setCurrent] = useState(null);

  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [role, setRole] = useState("manager");

  // Delete dialog state
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleteUser, setDeleteUser] = useState(null);

  const loadUsers = () => api.get("/users").then((r) => setUsers(r.data));

  useEffect(() => {
    loadUsers();
  }, []);

  const openModal = (u = null) => {
    setCurrent(u);
    setName(u?.name || "");
    setEmail(u?.email || "");
    setRole(u?.role || "manager");
    setPassword("");
    setOpen(true);
  };

  const save = async () => {
    const payload = { name, email, role };
    if (password) payload.password = password;
    try {
      current
        ? await api.put(`/users/${current.id}`, payload)
        : await api.post("/users", payload);

      setOpen(false);
      loadUsers();
      current
        ? notify("User updated successfully")
        : notify("User added successfully");
    } catch (err) {
      if (err.response?.status === 422) {
        const messages = Object.values(err.response.data.errors).flat();
        messages.forEach((msg) => notify(msg, "error"));
      } else {
        notify("Something went wrong", "error");
      }
    }
  };

  // Open delete dialog
  const confirmDelete = (user) => {
    setDeleteUser(user);
    setDeleteOpen(true);
  };

  const remove = async () => {
    if (!deleteUser) return;
    try {
      await api.delete(`/users/${deleteUser.id}`);
      notify("User deleted successfully");
      loadUsers();
    } catch (err) {
      notify("Failed to delete user", "error");
    } finally {
      setDeleteOpen(false);
      setDeleteUser(null);
    }
  };

  return (
    <Container maxWidth="xl">
      <Box display="flex" justifyContent="space-between" mb={2}>
        <Typography variant="h4" fontWeight={600}>
          Users
        </Typography>

        <Can role="admin">
          <Button variant="contained" onClick={() => openModal()}>
            Add User
          </Button>
        </Can>
      </Box>

      <Paper elevation={3} sx={{ borderRadius: 2 }}>
        <Table>
          <TableHead sx={{ background: "#111827" }}>
            <TableRow>
              <TableCell sx={{ color: "#fff" }}>
                <b>Name</b>
              </TableCell>
              <TableCell sx={{ color: "#fff" }}>
                <b>Email</b>
              </TableCell>
              <TableCell sx={{ color: "#fff" }}>
                <b>Role</b>
              </TableCell>
              <TableCell width={120} sx={{ color: "#fff" }}>
                <b>Actions</b>
              </TableCell>
            </TableRow>
          </TableHead>

          <TableBody>
            {users.map((u) => (
              <TableRow key={u.id} hover>
                <TableCell>{u.name}</TableCell>
                <TableCell>{u.email}</TableCell>
                <TableCell>
                  <Chip
                    label={u.role}
                    color={u.role === "admin" ? "primary" : "default"}
                    size="small"
                  />
                </TableCell>
                <TableCell>
                  {u.email !== "admin@customremaps.com" && (
                    <>
                      <IconButton onClick={() => openModal(u)}>
                        <EditIcon />
                      </IconButton>
                      <IconButton
                        color="error"
                        onClick={() => confirmDelete(u)}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </>
                  )}
                </TableCell>
              </TableRow>
            ))}

            {users.length === 0 && (
              <TableRow>
                <TableCell colSpan={4} align="center" sx={{ py: 4 }}>
                  <Typography color="text.secondary">No users found</Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </Paper>

      {/* User Form Modal */}
      <FormModal
        open={open}
        title={current ? "Edit User" : "Add User"}
        onClose={() => setOpen(false)}
        onSubmit={save}
      >
        <TextField
          fullWidth
          label="Name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          sx={{ mb: 2 }}
        />

        <TextField
          fullWidth
          label="Email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          sx={{ mb: 2 }}
        />

        <TextField
          select
          fullWidth
          label="Role"
          value={role}
          onChange={(e) => setRole(e.target.value)}
          sx={{ mb: 2 }}
        >
          <MenuItem value="admin">Admin</MenuItem>
          <MenuItem value="manager">Manager</MenuItem>
        </TextField>

        <TextField
          fullWidth
          label={current ? "New Password (optional)" : "Password"}
          type="password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
        />
      </FormModal>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteOpen} onClose={() => setDeleteOpen(false)} PaperProps={{
          sx: {
            background: "#111827",
            color: "#fff",
            borderRadius: 2,
          },
        }}>
        <DialogTitle>Delete User</DialogTitle>
        <DialogContent>
          Are you sure you want to delete <b>{deleteUser?.name}</b>?
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteOpen(false)}>Cancel</Button>
          <Button color="error" variant="contained"  onClick={remove}>
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
}
